import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  getAssociationPortalBySlug,
  resolveAssociationPortalHostname,
} from "src/api/association-portal";
import { APP_URL } from "src/config-global";
import {
  AssociationPortalManifest,
  PortalLocale,
  PortalTranslation,
} from "src/types/association-portal";

export type WhiteLabelNavItem = {
  title: string;
  path: string;
  icon?: string;
};

export type WhiteLabelConfig = {
  hideHeader?: boolean;
  hideFooter?: boolean;
  headerLogo?: string;
  compactLogo?: string;
  hideHeaderMenu?: boolean;
  headerMenuItems?: WhiteLabelNavItem[];
  primaryColor?: string;
  backgroundColor?: string;
  buttonStyle?: "rounded" | "square";
  portal?: AssociationPortalManifest;
  portalLoading: boolean;
  portalError: boolean;
  isCustomHost: boolean;
  providerUrltag?: string;
  primaryUrl?: string;
  currentTranslation?: PortalTranslation;
};

const defaultConfig: WhiteLabelConfig = {
  hideHeader: false,
  hideFooter: false,
  portalLoading: false,
  portalError: false,
  isCustomHost: false,
};

const STANDARD_PORTAL_NAVIGATION: Record<
  string,
  { defaultTitle: string; translationKey: string }
> = {
  home: { defaultTitle: "Home", translationKey: "home" },
  rankings: { defaultTitle: "Rankings", translationKey: "rankings" },
  tournaments: { defaultTitle: "Tournaments", translationKey: "tournaments" },
  news: { defaultTitle: "News", translationKey: "news" },
  live: { defaultTitle: "Live streaming", translationKey: "livestreaming" },
  rules: { defaultTitle: "Rules", translationKey: "rules" },
  register: { defaultTitle: "Registration", translationKey: "registration" },
};

export const WhiteLabelContext = createContext<WhiteLabelConfig>(defaultConfig);

function canonicalHostname(): string | null {
  if (!APP_URL) return null;
  try {
    return new URL(APP_URL).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function shouldResolveHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return !new Set([
    "localhost",
    "127.0.0.1",
    "app.foggosport.local",
    "app.foggosport.com",
    "foggosport.com",
    canonicalHostname(),
  ]).has(normalized);
}

function requestedProviderUrltag(pathname: string): string | undefined {
  const match = pathname.match(/^\/sportprovider\/([^/]+)/);
  if (!match) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
}

function portalTranslation(
  language: string,
  manifest: AssociationPortalManifest,
): PortalTranslation {
  const requested = language.split("-")[0] as PortalLocale;
  const fallback = manifest.translations[manifest.default_locale];
  const localized = manifest.translations[requested];
  if (!fallback) {
    throw new Error("association_portal_default_translation_missing");
  }
  if (!localized || localized.locale === fallback.locale) return fallback;

  const text = (localizedValue: string, fallbackValue: string) =>
    localizedValue.trim() ? localizedValue : fallbackValue;
  const blockIds = new Set([
    ...Object.keys(fallback.block_content),
    ...Object.keys(localized.block_content),
  ]);
  const blockContent = Object.fromEntries(
    Array.from(blockIds).map((blockId) => {
      const fallbackBlock = fallback.block_content[blockId] ?? {};
      const localizedBlock = localized.block_content[blockId] ?? {};
      const keys = new Set([
        ...Object.keys(fallbackBlock),
        ...Object.keys(localizedBlock),
      ]);
      return [
        blockId,
        Object.fromEntries(
          Array.from(keys).map((key) => [
            key,
            text(localizedBlock[key] ?? "", fallbackBlock[key] ?? ""),
          ]),
        ),
      ];
    }),
  );

  return {
    ...localized,
    organization_name: text(
      localized.organization_name,
      fallback.organization_name,
    ),
    logo_alt: text(localized.logo_alt, fallback.logo_alt),
    hero_title: text(localized.hero_title, fallback.hero_title),
    hero_summary: text(localized.hero_summary, fallback.hero_summary),
    contact_text: text(localized.contact_text, fallback.contact_text),
    footer_text: text(localized.footer_text, fallback.footer_text),
    seo_title: text(localized.seo_title, fallback.seo_title),
    seo_description: text(localized.seo_description, fallback.seo_description),
    block_content: blockContent,
  };
}

function portalNavigationPath(
  path: string,
  manifest: AssociationPortalManifest,
  isCustomHost: boolean,
): string {
  if (isCustomHost || path.startsWith("https://")) return path;
  const base = `/sportprovider/${encodeURIComponent(manifest.provider_urltag)}`;
  const canonicalPaths: Record<string, string> = {
    "/": `${base}/profile`,
    "/profile": `${base}/profile`,
    "/rankings": `${base}/rankings`,
    "/tournaments": `${base}/tournaments`,
    "/news": `${base}/newsfeed`,
    "/live": `${base}/livestreaming`,
    "/rules": `${base}/rules`,
    "/register": `${base}/register`,
  };
  return canonicalPaths[path] ?? path;
}

function aliasRedirectTarget(primaryUrl: string): string {
  const target = new URL(primaryUrl);
  target.pathname = window.location.pathname.startsWith("/")
    ? window.location.pathname
    : "/";
  const safeParameters = new URLSearchParams();
  let parameterCount = 0;
  new URLSearchParams(window.location.search).forEach((value, key) => {
    if (
      parameterCount >= 20 ||
      !/^[a-zA-Z0-9_.-]{1,64}$/.test(key) ||
      /(auth|code|email|password|session|token)/i.test(key) ||
      value.length > 500
    ) {
      return;
    }
    safeParameters.append(key, value);
    parameterCount += 1;
  });
  target.search = safeParameters.toString();
  target.hash = "";
  return target.toString();
}

type Props = {
  children: ReactNode;
};

export function WhiteLabelProvider({ children }: Props) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const providerUrltag = requestedProviderUrltag(location.pathname);
  const hostname = window.location.hostname.toLowerCase();
  // 034: on profile URLs the owning custom host wins over a supplied provider path.
  const publicPlayerRoute =
    /^\/(?:sportprovider\/[^/]+\/)?players\/[^/]+\/?$/.test(location.pathname);
  const resolveByHostname =
    (!providerUrltag || publicPlayerRoute) && shouldResolveHostname(hostname);
  const requestKey = `${hostname}:${providerUrltag || ""}:${resolveByHostname}`;
  const [resolvedKey, setResolvedKey] = useState("");
  const [manifest, setManifest] = useState<AssociationPortalManifest>();
  const [portalLoading, setPortalLoading] = useState(true);
  const [portalError, setPortalError] = useState(false);
  const [resolvedByCustomHost, setResolvedByCustomHost] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadPortal() {
      setPortalLoading(true);
      setPortalError(false);
      setManifest(undefined);
      setResolvedByCustomHost(false);

      if (!providerUrltag && !resolveByHostname) {
        setResolvedKey(requestKey);
        setPortalLoading(false);
        return;
      }

      try {
        const nextManifest = resolveByHostname
          ? await resolveAssociationPortalHostname(hostname)
          : await getAssociationPortalBySlug(providerUrltag!);
        if (!active) return;
        if (resolveByHostname && nextManifest.alias_redirect_url) {
          window.location.replace(
            aliasRedirectTarget(nextManifest.alias_redirect_url),
          );
          return;
        }
        setManifest(nextManifest);
        setResolvedByCustomHost(resolveByHostname);
      } catch {
        if (!active) return;
        setPortalError(true);
      } finally {
        if (active) {
          setResolvedKey(requestKey);
          setPortalLoading(false);
        }
      }
    }

    void loadPortal();
    return () => {
      active = false;
    };
  }, [hostname, providerUrltag, resolveByHostname, requestKey]);

  const value = useMemo<WhiteLabelConfig>(() => {
    if (resolvedKey !== requestKey) {
      return {
        ...defaultConfig,
        portalLoading: true,
        portalError: false,
        isCustomHost: false,
        providerUrltag,
      };
    }
    if (!manifest) {
      return {
        ...defaultConfig,
        portalLoading,
        portalError,
        isCustomHost: false,
        providerUrltag,
      };
    }
    const translation = portalTranslation(i18n.language, manifest);
    const useDarkLogo = manifest.theme.logo_variant === "dark";
    const primaryLogo = useDarkLogo
      ? manifest.brand.primary_logo_dark_url ?? manifest.brand.primary_logo_url
      : manifest.brand.primary_logo_url ?? manifest.brand.primary_logo_dark_url;
    const compactLogo = useDarkLogo
      ? manifest.brand.compact_logo_dark_url ?? manifest.brand.compact_logo_url
      : manifest.brand.compact_logo_url ?? manifest.brand.compact_logo_dark_url;
    const requestedLocale = i18n.language.split("-")[0] as PortalLocale;
    return {
      hideHeader: false,
      hideFooter: false,
      headerLogo: primaryLogo,
      compactLogo: compactLogo ?? primaryLogo,
      hideHeaderMenu: manifest.navigation.length === 0,
      headerMenuItems: manifest.navigation
        .filter((item) => item.enabled)
        .map((item) => {
          const localizedTitle =
            item.title_translations?.[requestedLocale]?.trim();
          const defaultTitle =
            item.title_translations?.[manifest.default_locale]?.trim() ||
            item.title;
          const standardItem = STANDARD_PORTAL_NAVIGATION[item.key];
          const standardLocalizedTitle =
            standardItem && item.title.trim() === standardItem.defaultTitle
              ? i18n.t(standardItem.translationKey)
              : undefined;

          return {
            title: localizedTitle || standardLocalizedTitle || defaultTitle,
            path: portalNavigationPath(
              item.path,
              manifest,
              resolvedByCustomHost,
            ),
            icon: item.icon,
          };
        }),
      primaryColor: manifest.theme.primary_color,
      backgroundColor: manifest.theme.background_color,
      buttonStyle: manifest.theme.button_style,
      portal: manifest,
      portalLoading,
      portalError,
      isCustomHost: resolvedByCustomHost,
      providerUrltag: manifest.provider_urltag,
      primaryUrl: manifest.primary_url,
      currentTranslation: translation,
    };
  }, [
    i18n.language,
    manifest,
    portalError,
    portalLoading,
    providerUrltag,
    resolvedByCustomHost,
    resolvedKey,
    requestKey,
  ]);

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
}

export const useWhiteLabel = () => useContext(WhiteLabelContext);
